import { inject, Injectable, signal } from "@angular/core";
import * as Sentry from "@sentry/angular";

import { PermissionDeniedError } from "@intocare/auth-data";
import { MustEnableMFAError, MustSetMainFunctionError, UnknownProblemError } from "@intocare/aws";
import { Address, DASupportInfo } from "@intocare/data-access/shared/organisation";
import { ENVIRONMENT_TOKEN, EnvironmentService } from "@intocare/environments";
import {
	Context,
	ContextNotInitializedError,
	ContextServiceInterface,
	ContextUser,
	FederatedEntity,
	FetchContextParameters,
	HqAccessInformation,
	HqOrganisationInformation,
	Problem,
	SupportedLocale,
	UnsupportedHqContextPropertyError,
} from "@intocare/util";

@Injectable({
	providedIn: "root",
})
export class ContextService implements ContextServiceInterface {
	private readonly environment = inject(ENVIRONMENT_TOKEN);
	private readonly environmentService = inject(EnvironmentService);

	private readonly _context = signal<Context>(defaultContext);
	public readonly context = this._context.asReadonly();

	public clearContext(): void {
		this._context.set(defaultContext);
	}

	private async fetchAccessInformation(token: string): Promise<HqAccessInformation> {
		return fetch(`${this.environmentService.getBaseUrlForContext("access")}/information`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
			.then((response) => {
				if (!response.ok) {
					Sentry.captureException(
						new Error(`Access information call failed with status ${response.status}: ${JSON.stringify(response)}`),
					);
					throw new Error("Failed to fetch access information");
				}

				return response.json() as Promise<HqAccessInformation>;
			})
			.catch((error) => {
				Sentry.captureException(`Access information call failed: ${JSON.stringify(error)}`);
				throw new Error("Failed to fetch access information");
			});
	}

	private async fetchOrganisationInformation(parameters: {
		token: string;
		userLocale: SupportedLocale;
	}): Promise<HqOrganisationInformation> {
		const { token, userLocale } = parameters;
		const applicationName = "HQ";

		return fetch(`${this.environmentService.getBaseUrlForContext("organisation")}/information`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Accept-Language": userLocale,
				"X-Application": `${applicationName}/${this.environment.VERSION}`,
			},
		})
			.then((response) => {
				if (!response.ok) {
					Sentry.captureException(
						new Error(
							`Organisation information call failed with status ${response.status}: ${JSON.stringify(response)}`,
						),
					);
					throw new Error("Failed to fetch organisation information");
				}

				return response.json() as Promise<HqOrganisationInformation>;
			})
			.catch((error) => {
				Sentry.captureException(`Organisation information call failed: ${JSON.stringify(error)}`);
				throw new Error("Failed to fetch organisation information");
			});
	}

	private async _fetchContext(token: string, hq: string): Promise<Context | undefined> {
		const contextInformation = await this.fetchAccessInformation(token);

		if (!contextInformation.active) {
			throw new Error("User is not active");
		}

		if ("problem" in contextInformation) {
			if (contextInformation.problem === Problem.MUST_ENABLE_MFA) {
				throw new MustEnableMFAError();
			}

			if (contextInformation.problem === Problem.MUST_SET_MAIN_FUNCTION) {
				throw new MustSetMainFunctionError();
			}

			throw new UnknownProblemError(`Unknown problem encountered: ${contextInformation.problem}`);
		}

		const { acceptedTermsAndConditions, permissions, roles, user, userLocale, mainFunction } = contextInformation;

		if (!permissions.length) {
			throw new PermissionDeniedError("No permissions found");
		}

		const organisationInformation = await this.fetchOrganisationInformation({ token, userLocale });
		const { timezone, supportedLocales, supportInfo } = organisationInformation;

		return {
			hq,
			user,
			supportedLocales,
			userLocale,
			acceptedTermsAndConditions,
			permissions,
			roles,
			mainFunction,
			get accessibleSites(): {
				id: string;
				home: string;
				name: string;
			}[] {
				throw new UnsupportedHqContextPropertyError("accessibleSites");
			},
			timezone,
			supportInfo,
			get modules(): {
				CallAlarms: boolean;
				Medication: boolean;
				OnboardResidents: boolean;
				RestraintsOverview: boolean;
				Dar: boolean;
				DietsV2: boolean;
				BelRAI: boolean;
				ManageDepartmentsAndRooms: boolean;
				ResidentApiSync: boolean;
				AllowMedicationGroupOverride: boolean;
				ValidationByHeadNurse: boolean;
			} {
				throw new UnsupportedHqContextPropertyError("modules");
			},
			get currentSite(): {
				id: string;
				home: string;
				name: string;
				address: Address;
				federatedEntity: FederatedEntity;
			} {
				throw new UnsupportedHqContextPropertyError("currentSite");
			},
		};
	}

	public async fetchContext(parameters: FetchContextParameters): Promise<Context> {
		const { token, hq } = parameters;

		if (parameters.home) {
			throw new Error('The "home" parameter is not supported for HQ context');
		}

		return this._fetchContext(token, hq)
			.then((context) => {
				this._context.set(context);

				return context;
			})
			.catch((error) => {
				this.clearContext();
				Sentry.captureException(error);
				throw error;
			});
	}
}

const defaultContext: Context = Object.freeze({
	get hq(): string {
		throw new ContextNotInitializedError();
	},
	get user(): ContextUser {
		throw new ContextNotInitializedError();
	},
	get supportedLocales(): SupportedLocale[] {
		throw new ContextNotInitializedError();
	},
	get userLocale(): SupportedLocale {
		throw new ContextNotInitializedError();
	},
	get acceptedTermsAndConditions(): boolean {
		throw new ContextNotInitializedError();
	},
	get permissions(): string[] {
		throw new ContextNotInitializedError();
	},
	get roles(): { id: string }[] {
		throw new ContextNotInitializedError();
	},
	get accessibleSites(): {
		id: string;
		home: string;
		name: string;
	}[] {
		throw new ContextNotInitializedError();
	},
	get timezone(): "Europe/Brussels" {
		throw new ContextNotInitializedError();
	},
	get supportInfo(): DASupportInfo {
		throw new ContextNotInitializedError();
	},
	get modules(): {
		CallAlarms: boolean;
		Medication: boolean;
		OnboardResidents: boolean;
		RestraintsOverview: boolean;
		Dar: boolean;
		DietsV2: boolean;
		BelRAI: boolean;
		ManageDepartmentsAndRooms: boolean;
		ResidentApiSync: boolean;
		AllowMedicationGroupOverride: boolean;
		ValidationByHeadNurse: boolean;
	} {
		throw new ContextNotInitializedError();
	},
	get currentSite(): {
		id: string;
		home: string;
		name: string;
		address: Address;
		federatedEntity: FederatedEntity;
	} {
		throw new ContextNotInitializedError();
	},
});
